import { useCallback, useMemo } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import type { MigratedCustomPageSlug } from '@shared/constants/customPageMigration';
import { MIGRATED_PAGE_TO_EVENT_FEATURE_FLAG } from '@shared/constants/customPageMigration';
import { toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';

export type MigratedCustomPageEventFeatureFlag = (typeof MIGRATED_PAGE_TO_EVENT_FEATURE_FLAG)[MigratedCustomPageSlug];

type UseMigratedCustomPageFeatureFlagsReturn = Readonly<{
  isLoading: boolean;
  hasData: boolean;
  enabledSlugs: ReadonlySet<MigratedCustomPageSlug>;
  isMigratedCustomPageEnabled: (slug: string) => boolean;
}>;

// Stable reference — Object.entries of a constant produces the same values each time.
const FLAG_ENTRIES = Object.entries(MIGRATED_PAGE_TO_EVENT_FEATURE_FLAG) as ReadonlyArray<[MigratedCustomPageSlug, string]>;

export const useMigratedCustomPageFeatureFlags = (): UseMigratedCustomPageFeatureFlagsReturn => {
  const { eventInfo, ssrEventInfo, loading: eventInfoLoading } = useEventInfo();

  // Read feature flags from EventInfo (already fetched by BasicEventInfo via EventInfoProvider).
  // During SSR initial load, eventInfo is undefined but ssrEventInfo.featureFlags is available
  // from the SSR preflight — use it as a fallback so migrated page detection works in Phase 1.
  const featureFlags = eventInfo?.featureFlags ?? ssrEventInfo?.featureFlags;
  const hasFlags = !!featureFlags;

  const enabledSlugs = useMemo(() => {
    if (!hasFlags) return new Set<MigratedCustomPageSlug>();

    const enabled = new Set<MigratedCustomPageSlug>();
    FLAG_ENTRIES.forEach(([slug, flag]) => {
      if (featureFlags?.includes(flag)) {
        enabled.add(slug);
      }
    });

    return enabled;
  }, [hasFlags, featureFlags]);

  const isMigratedCustomPageEnabled = useCallback(
    (slug: string) => {
      if (!slug || (!hasFlags && eventInfoLoading)) return false;
      // Canonicalize: migrated registrylinks pages carry pageSlug='registry' in data but their
      // canonical (flag-keyed) slug is 'registrylinks'. Callers often pass page.pageSlug directly.
      // toCanonicalMigratedSlug lowercases internally; fall back to lowercased input so
      // non-migrated slugs still match case-insensitively.
      const canonical = toCanonicalMigratedSlug(slug) ?? (slug.toLowerCase() as MigratedCustomPageSlug);
      return enabledSlugs.has(canonical);
    },
    [enabledSlugs, hasFlags, eventInfoLoading],
  );

  return {
    isLoading: eventInfoLoading,
    hasData: !!eventInfo,
    enabledSlugs,
    isMigratedCustomPageEnabled,
  };
};
